import { Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { FacilityStatus } from "generated-graphql/graphql";

export type FacilityStatusContext = {
  facilityStatus: FacilityStatus;
  facilityStatusReason?: string | null;
  facilityDateInactive?: string | null;
  facilityDateClosed?: string | null;
  facilityArchivedAt?: string | null;
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function FacilityStatusIndicator({
  facility,
  details = false,
}: {
  facility: FacilityStatusContext;
  details?: boolean;
}) {
  let effectiveDate: string | null = null;
  if (facility.facilityStatus === FacilityStatus.Inactive)
    effectiveDate = facility.facilityDateInactive ?? null;
  if (facility.facilityStatus === FacilityStatus.Closed)
    effectiveDate = facility.facilityDateClosed ?? null;
  const status = prettyPrintEnumValue(facility.facilityStatus);
  const summary = effectiveDate ? `${status} since ${formatDate(effectiveDate)}` : status;
  return (
    <Stack spacing={0.5}>
      {details && (
        <Typography variant="caption" color="text.secondary">
          Current facility status
        </Typography>
      )}
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Tooltip title={`Facility: ${summary}`}>
          <span>
            <StatusChip color="default" variant="outlined" label={status} />
          </span>
        </Tooltip>
        {facility.facilityArchivedAt && (
          <Tooltip title={`Archived ${formatDate(facility.facilityArchivedAt)}`}>
            <span>
              <StatusChip color="default" variant="outlined" label="Archived" />
            </span>
          </Tooltip>
        )}
      </Stack>
      {details && effectiveDate && (
        <Typography variant="body2">Effective {formatDate(effectiveDate)}</Typography>
      )}
      {details && facility.facilityStatusReason && (
        <Typography variant="body2">{facility.facilityStatusReason}</Typography>
      )}
      {details && facility.facilityArchivedAt && (
        <Typography variant="body2">Archived {formatDate(facility.facilityArchivedAt)}</Typography>
      )}
    </Stack>
  );
}
